/**
 * InfoTooltip — Click-to-show info popovers for property cards.
 *
 * Each property card has an info button with data-tooltip attribute.
 * Clicking shows a popover with description and default value.
 */

const TOOLTIP_CONTENT = {
  aufbau: {
    title: 'Aufbau',
    text: 'Mono = Einzelglas. 2-fach ISO = Isolierverglasung mit zwei Scheiben. 3-fach ISO = dreifach Isolierverglasung für maximale Dämmung.',
    default: '2-fach ISO'
  },
  hersteller: {
    title: 'Hersteller',
    text: 'Glas Trösch (Schweiz), AGC (Belgien) und Hero-Fire — zertifizierte Brandschutzglas-Hersteller. \"Alle\" zeigt alle Hersteller.',
    default: 'Alle (keine Einschränkung)'
  },
  klasse: {
    title: 'Brandschutzklasse',
    text: 'EI30/60/90 = Minuten Feuerwiderstand. Höher = mehr Schutz, aber schwerer und teurer.',
    default: 'EI30'
  },
  isolation: {
    title: 'Isolation (U-Wert)',
    text: 'Wärmedämmwert in W/(m²·K). Niedriger = besser isoliert. U 0.5 ist Spitzenklasse, U 5.4 ist Einfachverglasung.',
    default: '~U 1.2'
  },
  anwendung: {
    title: 'Anwendung',
    text: 'Wo wird das Glas eingebaut? Innen = Innenwand. Innen/Aussen = Fassade. Aussen/Aussen = freistehend.',
    default: 'Innen'
  },
  weissglas: {
    title: 'Weissglas (Extraweiss)',
    text: 'Eisenarm für maximale Klarheit und Farbneutralität. Aufpreis gegenüber Standardglas.',
    default: 'Nein'
  },
  widerstand: {
    title: 'Widerstandsklasse',
    text: 'RC 2–6 = Einbruchschutz-Stufe. RC 2 = Basisschutz, RC 6 = Hochsicherheit.',
    default: 'RC 2'
  },
  stossfugen: {
    title: 'Stossfugen',
    text: 'Rahmenlose Glas-an-Glas-Verbindung. Nur bei Mono-Verglasung möglich. Elegante Optik.',
    default: 'Nein'
  },
  absturzhemmung: {
    title: 'Absturzhemmung',
    text: 'Absturzsicherung nach EN 12600. Verhindert das Durchfallen bei bodentiefen Verglasungen oder Brüstungen.',
    default: 'Nein'
  },
  schall: {
    title: 'Schalldämmwert',
    text: 'Schalldämmung in Dezibel (dB). Höherer Wert = besserer Schallschutz. Wird automatisch aus dem gewählten Glastyp berechnet.',
    default: 'Nach Glastyp'
  },
  brandschutzglas: {
    title: 'Brandschutzglas',
    text: 'Hauptglas mit Brandschutzfunktion. Die Auswahl wird durch Eigenschaften (Klasse, Isolation, Hersteller) vorgefiltert.',
    default: 'Automatisch nach Eigenschaften'
  },
  abstandshalter: {
    title: 'Abstandshalter',
    text: 'Abstandshalter zwischen den Glasscheiben bei Isolierverglasung. Verfügbare Dicken: 8, 10, 12, 14, 16, 18, 20 mm. Grösserer Abstand = bessere Dämmung.',
    default: 'Nach U-Wert (z.B. U 1.2 → 14 mm)'
  },
  gas: {
    title: 'Gasfüllung',
    text: 'Edelgas im Scheibenzwischenraum für bessere Wärmedämmung. Krypton = Premiumdämmung. Argon setzt U-Wert auf 1.4. Mischgas setzt U-Wert auf 1.2.',
    default: 'Nach U-Wert'
  },
  mittelglas: {
    title: 'Mittleres Glas',
    text: 'Mittlere Scheibe bei 3-fach Isolierverglasung. Beschichtetes Glas für optimale Wärmedämmung.',
    default: 'Automatisch nach Aufbau'
  },
  aussenglas: {
    title: 'Äusseres Glas',
    text: 'Aussenscheibe der Isolierverglasung. Beschichtetes Glas für Wärme- und UV-Schutz.',
    default: 'Automatisch nach Aufbau'
  }
};

export default class InfoTooltip {
  constructor() {
    this.popover = document.getElementById('info-popover');
    this.titleEl = this.popover?.querySelector('.info-popover-title');
    this.textEl = this.popover?.querySelector('.info-popover-text');
    this.defaultEl = this.popover?.querySelector('.info-popover-default');
    this.activeBtn = null;

    if (!this.popover) return;

    this._initButtons();
    this._initClickOutside();
  }

  _initButtons() {
    document.querySelectorAll('.info-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const key = btn.dataset.tooltip;
        if (this.activeBtn === btn) {
          this._hide();
        } else {
          this._show(btn, key);
        }
      });
    });
  }

  _initClickOutside() {
    document.addEventListener('click', (e) => {
      if (this.activeBtn && !this.popover.contains(e.target)) {
        this._hide();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this._hide();
    });
  }

  _show(btn, key) {
    const content = TOOLTIP_CONTENT[key];
    if (!content) return;

    this.titleEl.textContent = content.title;
    this.textEl.textContent = content.text;
    this.defaultEl.textContent = `Standard: ${content.default}`;

    // Position below the button (relative to .configurator-grid)
    const rect = btn.getBoundingClientRect();
    const grid = document.querySelector('.configurator-grid');
    const gridRect = grid ? grid.getBoundingClientRect() : { left: 0, top: 0 };

    this.popover.style.left = `${rect.left - gridRect.left}px`;
    this.popover.style.top = `${rect.bottom - gridRect.top + 6}px`;

    this.popover.classList.remove('hidden');
    this.activeBtn = btn;
  }

  _hide() {
    this.popover.classList.add('hidden');
    this.activeBtn = null;
  }
}
